import {v4 as uuidv4} from "uuid";
import React from "react";
import type {TypographyProps} from '@mui/material/Typography';

type Variant = TypographyProps['variant'];
import type { CAEGlobalSettings } from "../../../types/CAEGlobalSettings";

export interface RichTextProps {
    textAsTree: RichTextElement,
    propertyName?: string,
    variant?: Variant,
    headlineElement?: string, // Element that should be rendered as headline
    wordsLimit?: number,
    charLimit?: number,
    noLinks?: boolean,
    strong?: boolean,
    color?: string,
    alignContent?: "center" | "right" | "left" | "inherit" | "justify" | undefined,
    collapseOnDoubleBreak?: boolean,
    docType?: "news" | "pressrelease" | "publication" | "article" | undefined,
    renderDownloadAsLink?: boolean,
    id?: string,
    isHideNavi?: boolean,
    metadataId?: string,
    caeGlobalSettings?: CAEGlobalSettings
}

export interface RichTextElement {
    _type: string
    name?: string
    attributes?: Array<Attribute>
    children?: Array<RichTextElement>
    data?: string
    uuid?: string
}

export interface Attribute {
    _type: string;
    name: string;
    value: string;
}

export type ListType = 'ul' | 'ol';

export function enrichWithUuid<T extends RichTextElement | null | undefined>(element: T): T {
    if (!element) return element;

    const clone = structuredClone(element) as RichTextElement;
    clone.uuid = uuidv4();

    if (clone.children && Array.isArray(clone.children)) {
        clone.children = clone.children.map((child) => enrichWithUuid(child)!);
    }
    return clone as T;
}
export function renderTruncatedElement(props: RichTextProps,
                                       renderElement: (...args: any[]) => JSX.Element | null,
                                       getTextChild: (...args: any[]) => string,
                                       showMore: boolean,
                                       setShowMore: (...args: any[]) => void,
                                       parentComponentId: string,
                                       pageId: string): JSX.Element {
    if (!props.textAsTree) {
        return <React.Fragment />;
    }

    const children: (JSX.Element | null)[] = [];
    let childTruncated: boolean = false;
    let charCount = 0;
    (props.textAsTree!.children ?? []).forEach((child: RichTextElement, index: number) => {
        if (!childTruncated) {
            const childElement = renderElement(props, child, parentComponentId, pageId, undefined, props.variant, showMore, setShowMore)
            const childText = getTextChild(childElement)
            charCount += childText.length
            const key = child.uuid ?? `${props.textAsTree.uuid ?? 'rt'}-${index}`;
            children.push(<React.Fragment key={key}>{childElement}</React.Fragment>)
            if (charCount > props.charLimit!) {
                childTruncated = true
            }
        }
    })
    return <React.Fragment key={props.textAsTree.uuid}>
        {children}
    </React.Fragment>
}

export function getElementTextAlign(props: RichTextProps, element: RichTextElement) {
    const className = getAttribute(element.attributes!, "class")
    if (className && isAlignClass(className)) {
        return getAlignClass(className)!.split('--')[1] as "center" | "right" | "left" | "justify";
    } else if (props.alignContent) {
        return props.alignContent;
    } else if (element.name!.startsWith('h')) {
        // Headline in article should be centered
        return 'center'
    } else {
        return "left";
    }
}

export function getAttribute(attributes: Array<Attribute>, key: string) {
    return attributes?.filter(a => a.name === key).map(a => a.value)[0] as string
}

export function findConsecutiveBrChildIndex(element: RichTextElement): number | undefined {
    const children = element.children;
    if (!children) return undefined;

    const index = children.findIndex((currentChild, currentIndex) => {
        const nextChild = children[currentIndex + 1] as RichTextElement | undefined;
        return currentChild.name === 'br' && nextChild?.name === 'br';
    });

    return index !== -1 ? index : undefined;
}

export function getTextChild(element: JSX.Element | null): string {
    if (!element) return ''
    if (typeof element.props.children === 'string') {
        return element.props.children;
    } else if (React.Children.count(element.props.children) > 0) {
        let result = '';
        React.Children.forEach(element.props.children, (child: JSX.Element | string) => {
            if (typeof child === 'string') {
                result += " " + child;
            } else {
                result += " " + getTextChild(child);
            }
        });
        return result;
    } else {
        return '';
    }
}

export function elementIncludesImage(element: RichTextElement): boolean {
    return !!(element.children && element.children.some(child => child.name === "img"));
}

export const isUrlAbsolute = (url: string) => {
    return (url.indexOf('://') > 0 || url.indexOf('//') === 0);
}

const CTA_BUTTON_CLASSES = ['cta-default', 'cta-blue', 'cta-yellow'];
const ALIGN_CLASSES = ['align--left', 'align--center', 'align--right', 'align--justify'];

const getClassTokens = (className: string) => className.trim().split(/\s+/).filter(Boolean);

export const getCtaButtonClass = (className: string) => {
    return getClassTokens(className).find(token => CTA_BUTTON_CLASSES.includes(token));
}

export const isCtaButtonClass = (className: string) => {
    return getCtaButtonClass(className) !== undefined;
}

export const getAlignClass = (className: string) => {
    return getClassTokens(className).find(token => ALIGN_CLASSES.includes(token));
}

export const isAlignClass = (className: string) => {
    return getAlignClass(className) !== undefined;
}